import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { FulfillmentDetails } from '@/hooks/bookings/types';
import { Calendar, Package, Truck, FileText, Edit, ExternalLink, User } from 'lucide-react';
import FulfillmentForm from './FulfillmentForm';
import { format } from 'date-fns';
import { useIsMobile } from '@/hooks/use-mobile';
interface FulfillmentCardProps {
  fulfillmentDetails: FulfillmentDetails | null;
  bookingId: string;
  isAdmin: boolean;
  onUpdate: () => Promise<void>;
}
const FulfillmentCard: React.FC<FulfillmentCardProps> = ({
  fulfillmentDetails,
  bookingId,
  isAdmin,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const isMobile = useIsMobile();

  // Debug logging
  console.log('FulfillmentCard - fulfillmentDetails:', fulfillmentDetails);
  const hasData = fulfillmentDetails && (fulfillmentDetails.waybill_number || fulfillmentDetails.courier_name || fulfillmentDetails.tracking_url || fulfillmentDetails.signature_url || fulfillmentDetails.estimated_delivery_date || fulfillmentDetails.attachments?.length > 0 || fulfillmentDetails.updates?.length > 0);
  if (!hasData && !isAdmin) {
    return null; // Don't show empty card to customers
  }
  const handleSave = async () => {
    await onUpdate();
    setIsEditing(false);
  };
  if (isEditing) {
    return <FulfillmentForm fulfillmentDetails={fulfillmentDetails} bookingId={bookingId} onSave={handleSave} onCancel={() => setIsEditing(false)} />;
  }
  return <motion.div animate={hasData ? {
    boxShadow: ["0 0 20px hsl(var(--primary) / 0.3), 0 0 40px hsl(var(--primary) / 0.1)", "0 0 25px hsl(var(--primary) / 0.35), 0 0 50px hsl(var(--primary) / 0.125)", "0 0 30px hsl(var(--secondary) / 0.4), 0 0 60px hsl(var(--secondary) / 0.15)", "0 0 25px hsl(var(--secondary) / 0.35), 0 0 50px hsl(var(--secondary) / 0.125)", "0 0 20px hsl(var(--primary) / 0.3), 0 0 40px hsl(var(--primary) / 0.1)"]
  } : {}} transition={{
    duration: 4,
    repeat: Infinity,
    ease: "easeInOut"
  }} className="rounded-lg">
      <Card className="bg-card border">
        <CardHeader className="pb-4">
        <div className="flex flex-col space-y-4">
          <div className={`flex items-center ${isMobile ? 'justify-center' : 'justify-between'}`}>
            <CardTitle className="font-semibold text-foreground flex items-center gap-2 text-base">
              <Truck className="h-5 w-5 text-primary" />
              Waybill & Fulfillment Details
            </CardTitle>
            {/* Desktop button */}
            {isAdmin && !isMobile && <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} className="h-9">
                <Edit className="h-4 w-4 mr-2" />
                {hasData ? 'Edit' : 'Add Details'}
              </Button>}
          </div>
          {/* Mobile button - full width on its own row */}
          {isAdmin && isMobile && <Button variant="outline" onClick={() => setIsEditing(true)} className="w-full h-11 min-h-[44px]">
              <Edit className="h-4 w-4 mr-2" />
              <span className="text-sm">{hasData ? 'Edit' : 'Add Details'}</span>
            </Button>}
        </div>
      </CardHeader>

      <CardContent className="space-y-6 relative">
        {!hasData ? <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm">No fulfillment details available yet.</p>
            {isAdmin && <p className="text-xs mt-2">Click "Add Details" to provide tracking information.</p>}
          </div> : <>
            {/* Waybill and Courier Info */}
            {(fulfillmentDetails?.waybill_number || fulfillmentDetails?.courier_name) && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fulfillmentDetails.waybill_number && <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">Waybill Number</label>
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                        {fulfillmentDetails.waybill_number}
                      </span>
                    </div>
                  </div>}

                {fulfillmentDetails.courier_name && <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">Courier</label>
                    <div className="flex items-center gap-2">
                      {fulfillmentDetails.courier_logo_url ? <img src={fulfillmentDetails.courier_logo_url} alt={fulfillmentDetails.courier_name} className="h-6 w-6 object-contain" /> : <Truck className="h-4 w-4 text-muted-foreground" />}
                      <span className="text-sm">{fulfillmentDetails.courier_name}</span>
                    </div>
                  </div>}
              </div>}

            {/* Tracking and Delivery Date */}
            {(fulfillmentDetails?.tracking_url || fulfillmentDetails?.estimated_delivery_date) && <>
                <Separator />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fulfillmentDetails.tracking_url && <div className="space-y-2">
                      <label className="text-sm font-medium text-muted-foreground">Tracking</label>
                      <Button variant="outline" size="sm" asChild className={`w-full justify-start ${isMobile ? 'h-11 min-h-[44px]' : ''}`}>
                        <a href={fulfillmentDetails.tracking_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Track Package
                        </a>
                      </Button>
                    </div>}

                  {fulfillmentDetails.estimated_delivery_date && <div className="space-y-2">
                      <label className="text-sm font-medium text-muted-foreground">Estimated Delivery</label>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          {format(new Date(fulfillmentDetails.estimated_delivery_date), 'PPP')}
                        </span>
                      </div>
                    </div>}
                </div>
              </>}

            {/* Signature */}
            {fulfillmentDetails?.signature_url && <>
                <Separator />
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Signature</label>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <Button variant="outline" size="sm" asChild className={isMobile ? 'h-11 min-h-[44px]' : ''}>
                      <a href={fulfillmentDetails.signature_url} target="_blank" rel="noopener noreferrer">
                        <FileText className="h-4 w-4 mr-2" />
                        View Signature
                      </a>
                    </Button>
                  </div>
                </div>
              </>}

            {/* Attachments */}
            {fulfillmentDetails?.attachments && fulfillmentDetails.attachments.length > 0 && <>
                <Separator />
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Attachments</label>
                   <div className={`grid gap-2 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
                     {fulfillmentDetails.attachments.map((attachment, index) => <Button key={index} variant="outline" size="sm" asChild className={`justify-start ${isMobile ? 'h-11 min-h-[44px]' : ''}`}>
                        <a href={attachment} target="_blank" rel="noopener noreferrer">
                          <FileText className="h-4 w-4 mr-2" />
                          Attachment {index + 1}
                        </a>
                      </Button>)}
                   </div>
                </div>
              </>}

            {/* Status Updates */}
            {fulfillmentDetails?.updates && fulfillmentDetails.updates.length > 0 && <>
                <Separator />
                <div className="space-y-3">
                  <label className="text-sm font-medium text-muted-foreground">Status Updates</label>
                  <div className="space-y-3">
                    {fulfillmentDetails.updates.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).map((update, index) => <div key={index} className="flex gap-3 p-3 bg-muted/50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="secondary" className="text-xs">
                                {update.status}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(update.timestamp), 'PPp')}
                              </span>
                            </div>
                            {update.notes && <p className="text-sm text-muted-foreground">{update.notes}</p>}
                          </div>
                        </div>)}
                  </div>
                </div>
              </>}
          </>}
        
        {/* Admin footnote */}
        {isAdmin && <div className={`text-xs text-muted-foreground/70 ${isMobile ? 'pt-4 text-center' : 'absolute bottom-0 right-0 py-[5px] px-[5px]'}`}>
            Trackable: waybill, courier, tracking URL, delivery date, signature, attachments, updates
          </div>}
      </CardContent>
    </Card>
    </motion.div>;
};
export default FulfillmentCard;